import { Box, SxProps, Theme, Typography } from '@mui/material';
import { AddButton, RemoveButton } from './productCardStyles';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { RootState } from '../../redux/store';
import { useAppSelector } from '../../redux/hooks';
import { useEffect, useState } from 'react';

interface QuantityControlProps {
  count: number;
  onAdd: () => void;
  onRemove: () => void;
  isEditable: boolean;
  fontSize?: 'small' | 'medium' | 'large';
}

export function QuantityControl({ count, onAdd, onRemove, isEditable, fontSize = 'medium' }: QuantityControlProps) {
  const [canAdd, setCanAdd] = useState<boolean>(false);
  const [canRemove, setCanRemove] = useState<boolean>(false);
  const updatingCart = useAppSelector((state: RootState) => state.shoppingCartState.updatingCart);

  useEffect(() => {
    setCanAdd(isEditable && !updatingCart);
    setCanRemove(isEditable && count > 0 && !updatingCart);
  }, [isEditable, updatingCart, count]);

  const sizeMap = {
    small: { icon: 25, font: 14 },
    medium: { icon: 30, font: 20 },
    large: { icon: 35, font: 24 },
  } as const;

  const metrics = sizeMap[fontSize] ?? sizeMap.medium;

  const iconSx = (isDisabled: boolean) =>
    ((theme: Theme) => ({
      height: `${metrics.icon}px`,
      width: `${metrics.icon}px`,
      color: isDisabled ? 'grey' : theme.palette.secondary.main,
    })) as SxProps<Theme>;

  const handleAdd = () => {
    if (canAdd) {
      onAdd();
    }
  };

  const handleRemove = () => {
    if (canRemove) {
      onRemove();
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-around',
        backgroundColor: 'white',
        width: '100%',
      }}
    >
      <RemoveButton disabled={!canRemove} onClick={handleRemove}>
        <RemoveCircleOutlineIcon sx={iconSx(!canRemove)} />
      </RemoveButton>
      <Typography
        sx={{
          marginTop: '5px',
          fontWeight: 'bold',
          fontSize: `${metrics.font}px`,
        }}
      >
        {count}
      </Typography>
      <AddButton disabled={!canAdd} onClick={handleAdd}>
        <AddCircleOutlineIcon sx={iconSx(!canAdd)} />
      </AddButton>
    </Box>
  );
}
